import React, { Component } from "react";

class PrivacyPolicyPage extends Component {
  constructor(props) {
    super(props);
    this.state = {};
  }

  render() {
    return (
      <div style={styles.wrapper}>
        <h1 style={styles.title}>Privacy Policy</h1>
        <p style={styles.lastUpdated}>Last updated July 24, 2026</p>

        <p>
          This Privacy Notice for Active Facility Monitoring, LLC ("we," "us," or "our"), describes how and why we
          might access, collect, store, use, and/or share ("process") your personal information when you use our
          services ("Services"), including when you:
        </p>
        <ul>
          <li>Download and use our mobile application (AFM Turbo), or any other application of ours that links to this Privacy Notice</li>
          <li>Use AFM Turbo mobile application as a portal into your AFM Enterprise software system. The AFM System is hosted by your employer and contains corporate data belonging to your employer.</li>
          <li>Engage with us in other related ways, including any marketing or events</li>
        </ul>
        <p>
          <strong>Questions or concerns?</strong> Reading this Privacy Notice will help you understand your privacy
          rights and choices. We are responsible for making decisions about how your personal information is processed.
          If you do not agree with our policies and practices, please do not use our Services. If you still have any
          questions or concerns, please contact us at <a href="mailto:privacy@afm.io">privacy@afm.io</a>.
        </p>

        <h2 style={styles.sectionHeading}>Summary of Key Points</h2>
        <p>
          This summary provides key points from our Privacy Notice, but you can find out more details about any of
          these topics by using our table of contents below to find the section you are looking for.
        </p>
        <ul>
          <li><strong>What personal information do we process?</strong> When you visit, use, or navigate our Services, we may process personal information depending on how you interact with us and the Services, the choices you make, and the products and features you use.</li>
          <li><strong>Do we process any sensitive personal information?</strong> We do not process sensitive personal information.</li>
          <li><strong>Do we collect any information from third parties?</strong> We do not collect any information from third parties.</li>
          <li><strong>How do we process your information?</strong> We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with law. We may also process your information for other purposes with your consent.</li>
          <li><strong>In what situations and with which parties do we share personal information?</strong> We may share information in specific situations and with specific third parties.</li>
          <li><strong>How do we keep your information safe?</strong> We have adequate organizational and technical processes and procedures in place to protect your personal information. However, no electronic transmission over the internet or information storage technology can be guaranteed to be 100% secure.</li>
          <li><strong>What are your rights?</strong> Depending on where you are located geographically, the applicable privacy law may mean you have certain rights regarding your personal information.</li>
          <li><strong>How do you exercise your rights?</strong> The easiest way to exercise your rights is by submitting a data subject access request, or by contacting us.</li>
        </ul>

        <h2 style={styles.sectionHeading}>Table of Contents</h2>
        <ol style={styles.toc}>
          <li><a href="#section1" style={styles.tocLink}>What Information Do We Collect?</a></li>
          <li><a href="#section2" style={styles.tocLink}>How Do We Process Your Information?</a></li>
          <li><a href="#section3" style={styles.tocLink}>When and With Whom Do We Share Your Personal Information?</a></li>
          <li><a href="#section4" style={styles.tocLink}>Do We Use Cookies and Other Tracking Technologies?</a></li>
          <li><a href="#section5" style={styles.tocLink}>How Long Do We Keep Your Information?</a></li>
          <li><a href="#section6" style={styles.tocLink}>How Do We Keep Your Information Safe?</a></li>
          <li><a href="#section7" style={styles.tocLink}>Do We Collect Information From Minors?</a></li>
          <li><a href="#section8" style={styles.tocLink}>What Are Your Privacy Rights?</a></li>
          <li><a href="#section9" style={styles.tocLink}>Controls for Do-Not-Track Features</a></li>
          <li><a href="#section10" style={styles.tocLink}>Do United States Residents Have Specific Privacy Rights?</a></li>
          <li><a href="#section11" style={styles.tocLink}>Do We Make Updates to This Notice?</a></li>
          <li><a href="#section12" style={styles.tocLink}>How Can You Contact Us About This Notice?</a></li>
          <li><a href="#section13" style={styles.tocLink}>How Can You Review, Update, or Delete the Data We Collect From You?</a></li>
        </ol>

        <hr style={styles.divider} />

        <h2 id="section1" style={styles.sectionHeading}>1. What Information Do We Collect?</h2>

        <h3 style={styles.subHeading}>Personal information you disclose to us</h3>
        <p><em>In Short: We collect personal information that you provide to us.</em></p>
        <p>
          We collect personal information that you voluntarily provide to us when you express an interest in obtaining
          information about us or our products and Services, when you participate in activities on the Services, or
          otherwise when you contact us.
        </p>
        <p><strong>Sensitive Information.</strong> We do not process sensitive information.</p>
        <p>
          <strong>Application Data.</strong> If you use our application(s), we also may collect the following
          information if you choose to provide us with access or permission:
        </p>
        <ul>
          <li><strong>Geolocation Information.</strong> We may request access or permission to track location-based information from your mobile device, either continuously or while you are using our mobile application(s), to provide certain location-based services. If you wish to change our access or permissions, you may do so in your device's settings.</li>
          <li><strong>Mobile Device Access.</strong> We may request access or permission to certain features from your mobile device, including your mobile device's bluetooth, camera, sensors, and other features. If you wish to change our access or permissions, you may do so in your device's settings.</li>
          <li><strong>Mobile Device Data.</strong> We automatically collect device information (such as your mobile device ID, model, and manufacturer), operating system, version information and system configuration information, device and application identification numbers, browser type and version, hardware model, Internet service provider and/or mobile carrier, and Internet Protocol (IP) address (or proxy server). If you are using our application(s), we may also collect information about the phone network associated with your mobile device, your mobile device's operating system or platform, the type of mobile device you use, your mobile device's unique device ID, and information about the features of our application(s) you accessed.</li>
        </ul>
        <p>
          This information is primarily needed to maintain the security and operation of our application(s), for
          troubleshooting, and for our internal analytics and reporting purposes.
        </p>
        <p>
          All personal information that you provide to us must be true, complete, and accurate, and you must notify
          us of any changes to such personal information.
        </p>

        <h3 style={styles.subHeading}>Information automatically collected</h3>
        <p><em>In Short: Some information — such as your Internet Protocol (IP) address and/or browser and device characteristics — is collected automatically when you visit our Services.</em></p>
        <p>
          We automatically collect certain information when you visit, use, or navigate the Services. This information
          does not reveal your specific identity (like your name or contact information) but may include device and
          usage information, such as your IP address, browser and device characteristics, operating system, language
          preferences, referring URLs, device name, country, location, information about how and when you use our
          Services, and other technical information. This information is primarily needed to maintain the security and
          operation of our Services, and for our internal analytics and reporting purposes.
        </p>
        <p>Like many businesses, we also collect information through cookies and similar technologies.</p>
        <p>The information we collect includes:</p>
        <ul>
          <li><strong>Log and Usage Data.</strong> Log and usage data is service-related, diagnostic, usage, and performance information our servers automatically collect when you access or use our Services and which we record in log files. Depending on how you interact with us, this log data may include your IP address, device information, browser type, and settings and information about your activity in the Services (such as the date/time stamps associated with your usage, pages and files viewed, searches, and other actions you take such as which features you use), device event information (such as system activity, error reports (sometimes called "crash dumps"), and hardware settings.</li>
          <li><strong>Device Data.</strong> We collect device data such as information about your computer, phone, tablet, or other device you use to access the Services. Depending on the device used, this device data may include information such as your IP address (or proxy server), device and application identification numbers, location, browser type, hardware model, Internet service provider and/or mobile carrier, operating system, and system configuration information.</li>
          <li><strong>Location Data.</strong> We collect location data such as information about your device's location, which can be either precise or imprecise. How much information we collect depends on the type and settings of the device you use to access the Services. For example, we may use GPS and other technologies to collect geolocation data that tells us your current location (based on your IP address). You can opt out of allowing us to collect this information either by refusing access to the information or by disabling your Location setting on your device. However, if you choose to opt out, you may not be able to use certain aspects of the Services.</li>
        </ul>

        <h2 id="section2" style={styles.sectionHeading}>2. How Do We Process Your Information?</h2>
        <p><em>In Short: We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with law. We may also process your information for other purposes with your consent.</em></p>
        <p>We process your personal information for a variety of reasons, depending on how you interact with our Services, including:</p>
        <ul>
          <li><strong>To deliver and facilitate delivery of services to the user.</strong> We may process your information to provide you with the requested service.</li>
          <li><strong>To evaluate and improve our Services, products, marketing, and your experience.</strong> We may process your information when we believe it is necessary to identify usage trends, determine the effectiveness of our promotional campaigns, and to evaluate and improve our Services, products, marketing, and your experience.</li>
        </ul>

        <h2 id="section3" style={styles.sectionHeading}>3. When and With Whom Do We Share Your Personal Information?</h2>
        <p><em>In Short: We may share information in specific situations described in this section and/or with the following third parties.</em></p>
        <p>We may need to share your personal information in the following situations:</p>
        <ul>
          <li><strong>Business Transfers.</strong> We may share or transfer your information in connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business to another company.</li>
          <li><strong>When we use Google Maps Platform APIs.</strong> We may share your information with certain Google Maps Platform APIs (e.g., Google Maps API, Places API). Google Maps uses GPS, Wi-Fi, and cell towers to estimate your location. GPS is accurate to about 20 meters, while Wi-Fi and cell towers help improve accuracy when GPS signals are weak, like indoors. This data helps Google Maps provide directions, but it is not always perfectly precise. We obtain and store on your device ("cache") your location. You may revoke your consent anytime by contacting us at the contact details provided at the end of this document.</li>
        </ul>

        <h2 id="section4" style={styles.sectionHeading}>4. Do We Use Cookies and Other Tracking Technologies?</h2>
        <p><em>In Short: We may use cookies and other tracking technologies to collect and store your information.</em></p>
        <p>
          We may use cookies and similar tracking technologies (like web beacons and pixels) to gather information
          when you interact with our Services. Some online tracking technologies help us maintain the security of our
          Services, prevent crashes, fix bugs, save your preferences, and assist with basic site functions.
        </p>
        <p>
          We also permit third parties and service providers to use online tracking technologies on our Services for
          analytics and advertising, including to help manage and display advertisements or to tailor advertisements
          to your interests. The third parties and service providers use their technology to provide advertising about
          products and services tailored to your interests which may appear either on our Services or on other websites.
        </p>
        <p>
          To the extent these online tracking technologies are deemed to be a "sale"/"sharing" (which includes
          targeted advertising, as defined under the applicable laws) under applicable US state laws, you can opt out
          of these online tracking technologies by submitting a request as described below under section 10.
        </p>
        <p>
          Specific information about how we use such technologies and how you can refuse certain cookies is set out
          in our Cookie Notice.
        </p>

        <h2 id="section5" style={styles.sectionHeading}>5. How Long Do We Keep Your Information?</h2>
        <p><em>In Short: We keep your information for as long as necessary to fulfill the purposes outlined in this Privacy Notice unless otherwise required by law.</em></p>
        <p>
          We will only keep your personal information for as long as it is necessary for the purposes set out in this
          Privacy Notice, unless a longer retention period is required or permitted by law (such as tax, accounting,
          or other legal requirements). No purpose in this notice will require us keeping your personal information
          for longer than a period of time determined by your employer.
        </p>
        <p>
          When we have no ongoing legitimate business need to process your personal information, we will either delete
          or anonymize such information, or, if this is not possible (for example, because your personal information
          has been stored in backup archives), then we will securely store your personal information and isolate it
          from any further processing until deletion is possible.
        </p>

        <h2 id="section6" style={styles.sectionHeading}>6. How Do We Keep Your Information Safe?</h2>
        <p><em>In Short: We aim to protect your personal information through a system of organizational and technical security measures.</em></p>
        <p>
          We have implemented appropriate and reasonable technical and organizational security measures designed to
          protect the security of any personal information we process. However, despite our safeguards and efforts to
          secure your information, no electronic transmission over the Internet or information storage technology can
          be guaranteed to be 100% secure, so we cannot promise or guarantee that hackers, cybercriminals, or other
          unauthorized third parties will not be able to defeat our security and improperly collect, access, steal, or
          modify your information. Although we will do our best to protect your personal information, transmission of
          personal information to and from our Services is at your own risk. You should only access the Services
          within a secure environment.
        </p>

        <h2 id="section7" style={styles.sectionHeading}>7. Do We Collect Information From Minors?</h2>
        <p><em>In Short: We do not knowingly collect data from or market to children under 18 years of age.</em></p>
        <p>
          We do not knowingly collect, solicit data from, or market to children under 18 years of age, nor do we
          knowingly sell such personal information. By using the Services, you represent that you are at least 18 or
          that you are the parent or guardian of such a minor dependent's use of the Services. If we learn that
          personal information from users less than 18 years of age has been collected, we will deactivate the account
          and take reasonable measures to promptly delete such data from our records. If you become aware of any data
          we may have collected from children under age 18, please contact us at{" "}
          <a href="mailto:privacy@afm.io">privacy@afm.io</a>.
        </p>

        <h2 id="section8" style={styles.sectionHeading}>8. What Are Your Privacy Rights?</h2>
        <p><em>In Short: You may review, change, or terminate your account at any time, depending on your country, province, or state of residence.</em></p>
        <p>
          <strong>Withdrawing your consent:</strong> If we are relying on your consent to process your personal
          information, which may be express and/or implied consent depending on the applicable law, you have the right
          to withdraw your consent at any time. You can withdraw your consent at any time by contacting us by using
          the contact details provided in the section "HOW CAN YOU CONTACT US ABOUT THIS NOTICE?" below.
        </p>
        <p>
          However, please note that this will not affect the lawfulness of the processing before its withdrawal nor,
          when applicable law allows, will it affect the processing of your personal information conducted in reliance
          on lawful processing grounds other than consent.
        </p>
        <p>
          If you have questions or comments about your privacy rights, you may email us at{" "}
          <a href="mailto:privacy@afm.io">privacy@afm.io</a>.
        </p>

        <h2 id="section9" style={styles.sectionHeading}>9. Controls for Do-Not-Track Features</h2>
        <p>
          Most web browsers and some mobile operating systems and mobile applications include a Do-Not-Track ("DNT")
          feature or setting you can activate to signal your privacy preference not to have data about your online
          browsing activities monitored and collected. At this stage, no uniform technology standard for recognizing
          and implementing DNT signals has been finalized. As such, we do not currently respond to DNT browser signals
          or any other mechanism that automatically communicates your choice not to be tracked online. If a standard
          for online tracking is adopted that we must follow in the future, we will inform you about that practice in
          a revised version of this Privacy Notice.
        </p>
        <p>
          California law requires us to let you know how we respond to web browser DNT signals. Because there
          currently is not an industry or legal standard for recognizing or honoring DNT signals, we do not respond
          to them at this time.
        </p>

        <h2 id="section10" style={styles.sectionHeading}>10. Do United States Residents Have Specific Privacy Rights?</h2>
        <p><em>In Short: If you are a resident of California, Colorado, Connecticut, Delaware, Florida, Indiana, Iowa, Kentucky, Maryland, Minnesota, Montana, Nebraska, New Hampshire, New Jersey, Oregon, Rhode Island, Tennessee, Texas, Utah, or Virginia, you may have the right to request access to and receive details about the personal information we maintain about you and how we have processed it, correct inaccuracies, get a copy of, or delete your personal information. These rights may be limited in some circumstances by applicable law.</em></p>

        <h3 style={styles.subHeading}>Categories of Personal Information We Collect</h3>
        <p>
          The table below shows the categories of personal information we have collected in the past twelve (12)
          months. For a comprehensive inventory of all personal information we process, please refer to section 1.
        </p>
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Category</th>
                <th style={styles.th}>Examples</th>
                <th style={{ ...styles.th, textAlign: "center" }}>Collected</th>
              </tr>
            </thead>
            <tbody>
              {[
                { cat: "A. Identifiers", ex: "Contact details, such as real name, alias, postal address, telephone or mobile contact number, unique personal identifier, online identifier, Internet Protocol address, email address, and account name", collected: "NO" },
                { cat: "B. Personal information as defined in the California Customer Records statute", ex: "Name, contact information, education, employment, employment history, and financial information", collected: "NO" },
                { cat: "C. Protected classification characteristics under state or federal law", ex: "Gender, age, date of birth, race and ethnicity, national origin, marital status, and other demographic data", collected: "NO" },
                { cat: "D. Commercial information", ex: "Transaction information, purchase history, financial details, and payment information", collected: "NO" },
                { cat: "E. Biometric information", ex: "Fingerprints and voiceprints", collected: "NO" },
                { cat: "F. Internet or other similar network activity", ex: "Browsing history, search history, online behavior, interest data, and interactions with our and other websites, applications, systems, and advertisements", collected: "NO" },
                { cat: "G. Geolocation data", ex: "Device location", collected: "YES" },
                { cat: "H. Audio, electronic, sensory, or similar information", ex: "Images and audio, video or call recordings created in connection with our business activities", collected: "NO" },
                { cat: "I. Professional or employment-related information", ex: "Business contact details in order to provide you our Services at a business level or job title, work history, and professional qualifications if you apply for a job with us", collected: "YES" },
                { cat: "J. Education Information", ex: "Student records and directory information", collected: "NO" },
                { cat: "K. Inferences drawn from collected personal information", ex: "Inferences drawn from any of the collected personal information listed above to create a profile or summary about, for example, an individual's preferences and characteristics", collected: "NO" },
                { cat: "L. Sensitive personal information", ex: "—", collected: "NO" },
              ].map((row, i) => (
                <tr key={i} style={i % 2 === 0 ? styles.trEven : styles.trOdd}>
                  <td style={styles.td}>{row.cat}</td>
                  <td style={styles.td}>{row.ex}</td>
                  <td style={{ ...styles.td, textAlign: "center", fontWeight: "bold", color: row.collected === "YES" ? "#2a7a2a" : "#555" }}>{row.collected}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p>We may also collect other personal information outside of these categories through instances where you interact with us in person, online, or by phone or mail in the context of:</p>
        <ul>
          <li>Receiving help through our customer support channels;</li>
          <li>Participation in customer surveys or contests; and</li>
          <li>Facilitation in the delivery of our Services and to respond to your inquiries.</li>
        </ul>
        <p>We will use and retain the collected personal information as needed to provide the Services or for:</p>
        <ul>
          <li>Category G — as determined by your employer</li>
          <li>Category I — As long as the user has an account with us</li>
        </ul>

        <h3 style={styles.subHeading}>Sources of Personal Information</h3>
        <p>Learn more about the sources of personal information we collect in section 1, "WHAT INFORMATION DO WE COLLECT?"</p>

        <h3 style={styles.subHeading}>How We Use and Share Personal Information</h3>
        <p>Learn more about how we use your personal information in section 2, "HOW DO WE PROCESS YOUR INFORMATION?"</p>
        <p>
          We may disclose your personal information with our service providers pursuant to a written contract between
          us and each service provider. We may use your personal information for our own business purposes, such as
          for undertaking internal research for technological development and demonstration. This is not considered to
          be "selling" of your personal information.
        </p>
        <p>
          We have not disclosed, sold, or shared any personal information to third parties for a business or commercial
          purpose in the preceding twelve (12) months. We will not sell or share personal information in the future
          belonging to website visitors, users, and other consumers.
        </p>

        <h3 style={styles.subHeading}>Your Rights</h3>
        <p>
          You have rights under certain US state data protection laws. However, these rights are not absolute, and in
          certain cases, we may decline your request as permitted by law. These rights include:
        </p>
        <ul>
          <li>Right to know whether or not we are processing your personal data</li>
          <li>Right to access your personal data</li>
          <li>Right to correct inaccuracies in your personal data</li>
          <li>Right to request the deletion of your personal data</li>
          <li>Right to obtain a copy of the personal data you previously shared with us</li>
          <li>Right to non-discrimination for exercising your rights</li>
          <li>Right to opt out of the processing of your personal data if it is used for targeted advertising, the sale of personal data, or profiling</li>
        </ul>
        <p>Depending upon the state where you live, you may also have the following rights:</p>
        <ul>
          <li>Right to access the categories of personal data being processed (as permitted by applicable law, including Minnesota)</li>
          <li>Right to obtain a list of the categories of third parties to which we have disclosed personal data (as permitted by applicable law, including California, Delaware, and Maryland)</li>
          <li>Right to obtain a list of specific third parties to which we have disclosed personal data (as permitted by applicable law, including Minnesota and Oregon)</li>
          <li>Right to obtain a list of third parties to which we have sold personal data (as permitted by applicable law, including Connecticut)</li>
          <li>Right to review, understand, question, and depending on where you live, correct how personal data has been profiled (as permitted by applicable law, including Connecticut and Minnesota)</li>
          <li>Right to limit use and disclosure of sensitive personal data (as permitted by applicable law, including California)</li>
          <li>Right to opt out of the collection of sensitive data and personal data collected through the operation of a voice or facial recognition feature (as permitted by applicable law, including Florida)</li>
        </ul>

        <h3 style={styles.subHeading}>How to Exercise Your Rights</h3>
        <p>
          To exercise these rights, you can contact us by submitting a data subject access request, by emailing us
          at <a href="mailto:privacy@afm.io">privacy@afm.io</a>, or by referring to the contact details at the bottom
          of this document.
        </p>
        <p>
          Under certain US state data protection laws, you can designate an authorized agent to make a request on
          your behalf. We may deny a request from an authorized agent that does not submit proof that they have been
          validly authorized to act on your behalf in accordance with applicable laws.
        </p>

        <h3 style={styles.subHeading}>Request Verification</h3>
        <p>
          Upon receiving your request, we will need to verify your identity to determine you are the same person about
          whom we have the information in our system. We will only use personal information provided in your request
          to verify your identity or authority to make the request. However, if we cannot verify your identity from
          the information already maintained by us, we may request that you provide additional information for the
          purposes of verifying your identity and for security or fraud-prevention purposes.
        </p>
        <p>
          If you submit the request through an authorized agent, we may need to collect additional information to
          verify your identity before processing your request and the agent will need to provide a written and signed
          permission from you to submit such request on your behalf.
        </p>

        <h3 style={styles.subHeading}>Appeals</h3>
        <p>
          Under certain US state data protection laws, if we decline to take action regarding your request, you may
          appeal our decision by emailing us at <a href="mailto:privacy@afm.io">privacy@afm.io</a>. We will inform
          you in writing of any action taken or not taken in response to the appeal, including a written explanation
          of the reasons for the decisions. If your appeal is denied, you may submit a complaint to your state
          attorney general.
        </p>

        <h2 id="section11" style={styles.sectionHeading}>11. Do We Make Updates to This Notice?</h2>
        <p><em>In Short: Yes, we will update this notice as necessary to stay compliant with relevant laws.</em></p>
        <p>
          We may update this Privacy Notice from time to time. The updated version will be indicated by an updated
          "Revised" date at the top of this Privacy Notice. If we make material changes to this Privacy Notice, we
          may notify you either by prominently posting a notice of such changes or by directly sending you a
          notification. We encourage you to review this Privacy Notice frequently to be informed of how we are
          protecting your information.
        </p>

        <h2 id="section12" style={styles.sectionHeading}>12. How Can You Contact Us About This Notice?</h2>
        <p>
          If you have questions or comments about this notice, you may email us at{" "}
          <a href="mailto:privacy@afm.io">privacy@afm.io</a>.
        </p>

        <h2 id="section13" style={styles.sectionHeading}>13. How Can You Review, Update, or Delete the Data We Collect From You?</h2>
        <p>
          Based on the applicable laws of your country or state of residence in the US, you may have the right to
          request access to the personal information we collect from you, details about how we have processed it,
          correct inaccuracies, or delete your personal information. You may also have the right to withdraw your
          consent to our processing of your personal information. These rights may be limited in some circumstances
          by applicable law. To request to review, update, or delete your personal information, please fill out and
          submit a data subject access request.
        </p>
      </div>
    );
  }
}

const styles = {
  wrapper: {
    margin: "auto",
    maxWidth: 800,
    padding: "20px 30px 60px",
    fontFamily: "'Georgia', serif",
    color: "#222",
    lineHeight: 1.75,
    fontSize: 16,
  },
  title: {
    textAlign: "center",
    fontSize: 32,
    fontWeight: "bold",
    marginBottom: 4,
    marginTop: 32,
  },
  lastUpdated: {
    textAlign: "center",
    color: "#777",
    fontSize: 14,
    marginBottom: 32,
  },
  sectionHeading: {
    fontSize: 20,
    fontWeight: "bold",
    marginTop: 40,
    marginBottom: 12,
    borderBottom: "2px solid #e0e0e0",
    paddingBottom: 6,
    textTransform: "uppercase",
    letterSpacing: "0.03em",
  },
  subHeading: {
    fontSize: 17,
    fontWeight: "bold",
    marginTop: 28,
    marginBottom: 8,
  },
  toc: {
    lineHeight: 2,
    paddingLeft: 24,
  },
  tocLink: {
    color: "#1a73e8",
    textDecoration: "none",
  },
  divider: {
    border: "none",
    borderTop: "1px solid #ddd",
    margin: "32px 0",
  },
  tableWrapper: {
    overflowX: "auto",
    marginTop: 16,
    marginBottom: 16,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 14,
  },
  th: {
    backgroundColor: "#f0f0f0",
    border: "1px solid #ccc",
    padding: "10px 14px",
    textAlign: "left",
    fontWeight: "bold",
  },
  td: {
    border: "1px solid #ccc",
    padding: "9px 14px",
    verticalAlign: "top",
  },
  trEven: {
    backgroundColor: "#fff",
  },
  trOdd: {
    backgroundColor: "#fafafa",
  },
};

export default PrivacyPolicyPage;
